import './state.css'

/* The loading state (state system, 2026-09-04). The shell paints first;
 * the page area fills with a skeleton at the height the content will
 * take — rows, cards, lines or the five queue tiles — so nothing jumps
 * when the read lands and the page is never white after a navigation.
 * No spinner, no "loading…" text: the shape says it. Rendered on the
 * first paint, so it is on screen well inside the 300 ms rule. */

type Kind = 'rows' | 'cards' | 'lines' | 'tiles'

interface Props {
  /** rows = one bordered row each · cards = a bordered card with three
   * bars · lines = bare bars (a drawer section) · tiles = the workbench's
   * five-up grid. */
  kind?: Kind
  /** How many rows / cards / lines / tiles. */
  n?: number
  /** The real height of one row or card, in px. Lines and tiles have
   * their own. */
  height?: number
  testId?: string
  className?: string
}

const DEFAULT_N: Record<Kind, number> = { rows: 6, cards: 3, lines: 4, tiles: 5 }
const DEFAULT_H: Record<Kind, number> = { rows: 41, cards: 110, lines: 12, tiles: 148 }

/* Bar widths per card — three bars, the last two shorter, like a sentence
 * and its second line. Rows take one long and one short bar. */
const CARD_BARS = ['62%', '84%', '46%']
const ROW_BARS = ['44%', '22%']

export default function Skeleton({ kind = 'rows', n, height, testId, className }: Props) {
  const count = n ?? DEFAULT_N[kind]
  const h = height ?? DEFAULT_H[kind]
  const items = Array.from({ length: count }, (_, i) => i)
  return (
    <div
      className={`state-skel state-skel-${kind}${className ? ` ${className}` : ''}`}
      role="status"
      aria-busy="true"
      aria-label="Loading"
      data-testid={testId ?? 'state-skeleton'}
    >
      {items.map((i) =>
        kind === 'lines' ? (
          <span
            key={i}
            className="state-bar-sk"
            style={{ height: h, width: `${i % 2 ? 58 : 82}%` }}
          />
        ) : kind === 'tiles' ? (
          <div key={i} className="state-skel-item" style={{ minHeight: h }}>
            <span className="state-bar-sk" style={{ width: '34%', height: 26 }} />
            <span className="state-bar-sk" style={{ width: '70%' }} />
            <span className="state-bar-sk" style={{ width: '48%' }} />
          </div>
        ) : (
          <div key={i} className="state-skel-item" style={{ height: h }}>
            {(kind === 'cards' ? CARD_BARS : ROW_BARS).map((w, j) => (
              <span key={j} className="state-bar-sk" style={{ width: w }} />
            ))}
          </div>
        )
      )}
    </div>
  )
}
